import React, { useState, useEffect, useRef } from 'react';
import { useAIModel } from '../contexts/AIModelContext';
import ModelSelector from './ModelSelector';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  ArrowRight, TrendingUp, TrendingDown, Calculator, 
  Plus, Trash2, Layers, Brain, Zap, RefreshCw,
  CheckCircle2, XCircle, AlertTriangle, ArrowLeftRight,
  DollarSign, Target, Percent, Download, Timer, CandlestickChart
} from 'lucide-react';
import { toast } from 'sonner';
import TradingChartModal from './TradingChartModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const createEmptyLeg = () => ({
  option_type: 'call',
  position_type: 'long',
  strike: '',
  quantity: 1,
  premium: ''
});

const LegInputRow = ({ leg, index, onUpdate, onRemove, canRemove, label }) => (
  <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-semibold text-gray-600">{label} {index + 1}</span>
      {canRemove && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      )}
    </div>
    <div className="grid grid-cols-5 gap-2">
      <Select value={leg.option_type} onValueChange={(v) => onUpdate(index, 'option_type', v)}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="call">Call</SelectItem>
          <SelectItem value="put">Put</SelectItem>
        </SelectContent>
      </Select>
      <Select value={leg.position_type} onValueChange={(v) => onUpdate(index, 'position_type', v)}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="long">Long</SelectItem>
          <SelectItem value="short">Short</SelectItem>
        </SelectContent>
      </Select>
      <Input
        type="number"
        step="0.5"
        placeholder="Strike"
        value={leg.strike}
        onChange={(e) => onUpdate(index, 'strike', e.target.value)}
        className="h-8 text-xs"
      />
      <Input
        type="number"
        min="1"
        placeholder="Qty"
        value={leg.quantity}
        onChange={(e) => onUpdate(index, 'quantity', e.target.value)}
        className="h-8 text-xs"
      />
      <Input
        type="number"
        step="0.01"
        placeholder="Premium"
        value={leg.premium}
        onChange={(e) => onUpdate(index, 'premium', e.target.value)}
        className="h-8 text-xs"
      />
    </div>
  </div>
);

const RollCalculator = ({ initialPosition }) => {
  const [symbol, setSymbol] = useState('');
  const [currentExpiration, setCurrentExpiration] = useState('');
  const [newExpiration, setNewExpiration] = useState('');
  const [currentPrice, setCurrentPrice] = useState(null);
  const [fetchingPremiums, setFetchingPremiums] = useState(false);
  
  // Chart modal state
  const [isChartOpen, setIsChartOpen] = useState(false);
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [lastRefresh, setLastRefresh] = useState(null);
  const autoRefreshRef = useRef(null);
  
  // Current position legs
  const [currentLegs, setCurrentLegs] = useState([createEmptyLeg()]);
  
  // New position legs (what they want to roll to)
  const [newLegs, setNewLegs] = useState([createEmptyLeg()]);
  
  const { model: aiModel, setModel: setAiModel } = useAIModel();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Load initial position from portfolio
  useEffect(() => {
    if (!initialPosition) return;
    
    // Check if this is a spread (multiple legs) or single position
    const isSpread = initialPosition.is_spread || initialPosition.legs?.length > 1;
    
    if (isSpread && initialPosition.legs) {
      // Handle spread/multi-leg position
      setSymbol(initialPosition.underlying_symbol || '');
      
      const expDate = initialPosition.expiration_date || initialPosition.legs[0]?.expiration_date;
      if (expDate) {
        setCurrentExpiration(new Date(expDate).toISOString().split('T')[0]);
      }
      
      const newLegs = initialPosition.legs.map(leg => ({
        option_type: leg.option_type || 'call',
        position_type: leg.quantity > 0 ? 'long' : 'short',
        strike: leg.strike_price?.toString() || '',
        quantity: Math.abs(leg.quantity) || 1,
        premium: leg.average_open_price?.toString() || ''
      }));
      setCurrentLegs(newLegs);
      setCurrentPrice(initialPosition.legs[0]?.close_price);
      
      toast.info(`Loaded ${initialPosition.strategy_name || 'spread'}! Now set up the new position you want to roll to.`);
      
    } else if (initialPosition.is_option) {
      // Handle single-leg option
      setSymbol(initialPosition.underlying_symbol || '');
      
      if (initialPosition.expiration_date) {
        const expDate = new Date(initialPosition.expiration_date);
        setCurrentExpiration(expDate.toISOString().split('T')[0]);
      }
      
      const newLeg = {
        option_type: initialPosition.option_type || 'call',
        position_type: initialPosition.quantity > 0 ? 'long' : 'short',
        strike: initialPosition.strike_price?.toString() || '',
        quantity: Math.abs(initialPosition.quantity) || 1,
        premium: initialPosition.average_open_price?.toString() || ''
      };
      setCurrentLegs([newLeg]);
      setCurrentPrice(initialPosition.close_price);
      
      toast.info('Position loaded! Now set up the new position you want to roll to.');
    }
  }, [initialPosition]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh && symbol && (currentExpiration || newExpiration)) {
      // Initial fetch
      if (currentExpiration && currentLegs.some(l => l.strike)) {
        fetchCurrentPremiums(true, true);
      }
      if (newExpiration && newLegs.some(l => l.strike)) {
        fetchCurrentPremiums(false, true);
      }
      
      // Set up interval
      autoRefreshRef.current = setInterval(() => {
        if (currentExpiration && currentLegs.some(l => l.strike)) {
          fetchCurrentPremiums(true, true);
        }
        if (newExpiration && newLegs.some(l => l.strike)) {
          fetchCurrentPremiums(false, true);
        }
      }, refreshInterval * 1000);
      
      return () => {
        if (autoRefreshRef.current) {
          clearInterval(autoRefreshRef.current);
        }
      };
    } else {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    }
  }, [autoRefresh, refreshInterval, symbol, currentExpiration, newExpiration]);

  // Current position leg management
  const addCurrentLeg = () => {
    if (currentLegs.length < 4) {
      setCurrentLegs([...currentLegs, createEmptyLeg()]);
    }
  };

  const removeCurrentLeg = (index) => {
    if (currentLegs.length > 1) {
      setCurrentLegs(currentLegs.filter((_, i) => i !== index));
    }
  };

  const updateCurrentLeg = (index, field, value) => {
    const newLegs = [...currentLegs];
    newLegs[index] = { ...newLegs[index], [field]: value };
    setCurrentLegs(newLegs);
  };

  // New position leg management
  const addNewLeg = () => {
    if (newLegs.length < 4) {
      setNewLegs([...newLegs, createEmptyLeg()]);
    }
  };

  const removeNewLeg = (index) => {
    if (newLegs.length > 1) {
      setNewLegs(newLegs.filter((_, i) => i !== index));
    }
  };

  const updateNewLeg = (index, field, value) => {
    const legs = [...newLegs];
    legs[index] = { ...legs[index], [field]: value };
    setNewLegs(legs);
  };

  // Copy current to new (as starting point)
  const copyCurrentToNew = () => {
    setNewLegs(currentLegs.map(leg => ({ ...leg, premium: '' })));
    toast.info('Copied current position structure to new position');
  };

  // Fetch current premiums for all legs quickly
  const fetchCurrentPremiums = async (forCurrent = true, silent = false) => {
    const legs = forCurrent ? currentLegs : newLegs;
    const exp = forCurrent ? currentExpiration : newExpiration;
    
    if (!symbol || !exp) {
      if (!silent) toast.error(`Please enter symbol and ${forCurrent ? 'current' : 'new'} expiration first`);
      return;
    }
    
    // Filter legs that have strikes
    const legsWithStrikes = legs.filter(leg => leg.strike);
    if (legsWithStrikes.length === 0) {
      if (!silent) toast.error('Please enter at least one strike price');
      return;
    }
    
    if (!silent) setFetchingPremiums(true);
    if (!silent) toast.info('Fetching current premiums...');
    
    try {
      const response = await axios.post(`${API}/data/premiums`, {
        symbol: symbol.toUpperCase(),
        expiration: exp,
        legs: legsWithStrikes.map(leg => ({
          strike: parseFloat(leg.strike),
          option_type: leg.option_type
        }))
      }, { timeout: 15000 });
      
      // Update legs with fetched premiums
      const premiumMap = {};
      response.data.legs.forEach(leg => {
        if (leg.premium) {
          premiumMap[`${leg.strike}_${leg.option_type}`] = leg.premium;
        }
      });
      
      const updatedLegs = legs.map(leg => {
        const key = `${leg.strike}_${leg.option_type}`;
        if (premiumMap[key]) {
          return { ...leg, premium: premiumMap[key].toFixed(2) };
        }
        return leg;
      });
      
      if (forCurrent) {
        setCurrentLegs(updatedLegs);
      } else {
        setNewLegs(updatedLegs);
      }
      
      setCurrentPrice(response.data.current_price);
      setLastRefresh(new Date());
      
      const found = response.data.legs.filter(l => l.found).length;
      if (!silent) toast.success(`Fetched ${found}/${legsWithStrikes.length} premiums. Stock: $${response.data.current_price}`);
    } catch (error) {
      console.error('Error fetching premiums:', error);
      if (!silent) toast.error('Failed to fetch premiums. Please enter manually.');
    } finally {
      if (!silent) setFetchingPremiums(false);
    }
  };

  const calculateRoll = async () => {
    // Validation
    if (!symbol) {
      toast.error('Please enter a stock symbol');
      return;
    }
    if (!currentExpiration || !newExpiration) {
      toast.error('Please enter both expiration dates');
      return;
    }

    // Validate current legs
    for (let i = 0; i < currentLegs.length; i++) {
      if (!currentLegs[i].strike || !currentLegs[i].premium) {
        toast.error(`Please fill in all fields for Current Position Leg ${i + 1}`);
        return;
      }
    }

    // Validate new legs
    for (let i = 0; i < newLegs.length; i++) {
      if (!newLegs[i].strike) {
        toast.error(`Please fill in strike for New Position Leg ${i + 1}`);
        return;
      }
    }

    setLoading(true);
    toast.info('Calculating roll outcome and P&L...');

    try {
      const response = await axios.post(`${API}/roll-calculator/calculate`, {
        symbol: symbol.toUpperCase(),
        current_position: {
          expiration: currentExpiration,
          legs: currentLegs.map(leg => ({
            option_type: leg.option_type,
            position_type: leg.position_type,
            strike: parseFloat(leg.strike),
            quantity: parseInt(leg.quantity) || 1,
            premium: parseFloat(leg.premium)
          }))
        },
        new_position: {
          expiration: newExpiration,
          legs: newLegs.map(leg => ({
            option_type: leg.option_type,
            position_type: leg.position_type,
            strike: parseFloat(leg.strike),
            quantity: parseInt(leg.quantity) || 1,
            premium: leg.premium ? parseFloat(leg.premium) : null
          }))
        },
        ai_model: aiModel
      }, { timeout: 90000 });

      setResult(response.data);
      toast.success('Roll calculation complete!');
    } catch (error) {
      console.error('Error calculating roll:', error);
      toast.error(error.response?.data?.detail || 'Failed to calculate roll');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="glass-effect border-0 shadow-lg bg-gradient-to-r from-cyan-50 to-blue-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl">
                <ArrowLeftRight className="h-8 w-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">Roll Comparison Calculator</CardTitle>
                <CardDescription className="text-base">
                  Compare your current position with a proposed roll to see the outcome and P&L difference
                </CardDescription>
              </div>
            </div>
            {/* Auto-refresh toggle */}
            <div className="flex items-center space-x-4 bg-white/50 rounded-lg px-4 py-2">
              <div className="flex items-center space-x-2">
                <Timer className={`h-4 w-4 ${autoRefresh ? 'text-green-600 animate-pulse' : 'text-gray-400'}`} />
                <Label htmlFor="auto-refresh" className="text-sm font-medium cursor-pointer">
                  Auto-refresh
                </Label>
                <Switch
                  id="auto-refresh"
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                  data-testid="auto-refresh-toggle"
                />
              </div>
              {autoRefresh && (
                <Select value={String(refreshInterval)} onValueChange={(v) => setRefreshInterval(Number(v))}>
                  <SelectTrigger className="w-20 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15s</SelectItem>
                    <SelectItem value="30">30s</SelectItem>
                    <SelectItem value="60">1m</SelectItem>
                    <SelectItem value="120">2m</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {autoRefresh && lastRefresh && (
                <span className="text-xs text-gray-500">
                  Last: {lastRefresh.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Input Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Position */}
        <Card className="glass-effect border-0 shadow-lg border-t-4 border-red-400">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-lg text-red-700">
              <XCircle className="h-5 w-5 mr-2" />
              Current Position (Close This)
            </CardTitle>
            <CardDescription>Enter your currently held position details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Symbol</Label>
                <div className="flex space-x-2">
                  <Input
                    placeholder="e.g., AAPL"
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                    className="font-semibold"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => symbol && setIsChartOpen(true)}
                    disabled={!symbol}
                    className="px-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0"
                    title="View Live Chart"
                    data-testid="view-chart-btn"
                  >
                    <CandlestickChart className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Expiration</Label>
                <Input
                  type="date"
                  value={currentExpiration}
                  onChange={(e) => setCurrentExpiration(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Position Legs ({currentLegs.length})</Label>
                <div className="flex space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fetchCurrentPremiums(true)}
                    disabled={fetchingPremiums || !symbol || !currentExpiration}
                    className="h-7 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700"
                    data-testid="fetch-current-premiums-btn"
                  >
                    {fetchingPremiums ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                    Fetch Premiums
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addCurrentLeg}
                    disabled={currentLegs.length >= 4}
                    className="h-7 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add Leg
                  </Button>
                </div>
              </div>
              {currentLegs.map((leg, index) => (
                <LegInputRow
                  key={index}
                  leg={leg}
                  index={index}
                  onUpdate={updateCurrentLeg}
                  onRemove={removeCurrentLeg}
                  canRemove={currentLegs.length > 1}
                  label="Leg"
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* New Position */}
        <Card className="glass-effect border-0 shadow-lg border-t-4 border-green-400">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-lg text-green-700">
              <CheckCircle2 className="h-5 w-5 mr-2" />
              New Position (Open This)
            </CardTitle>
            <CardDescription>Enter the position you want to roll to</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Symbol</Label>
                <Input
                  value={symbol}
                  disabled
                  className="bg-gray-100 font-semibold"
                />
              </div>
              <div>
                <Label className="text-xs">New Expiration</Label>
                <Input
                  type="date"
                  value={newExpiration}
                  onChange={(e) => setNewExpiration(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Position Legs ({newLegs.length})</Label>
                <div className="flex space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fetchCurrentPremiums(false)}
                    disabled={fetchingPremiums || !symbol || !newExpiration}
                    className="h-7 text-xs bg-green-50 hover:bg-green-100 text-green-700"
                    data-testid="fetch-new-premiums-btn"
                  >
                    {fetchingPremiums ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                    Fetch Premiums
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={copyCurrentToNew}
                    className="h-7 text-xs"
                  >
                    Copy Current
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addNewLeg}
                    disabled={newLegs.length >= 4}
                    className="h-7 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add Leg
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-500">Click "Fetch Premiums" or leave empty for auto-fetch during calculation</p>
              {newLegs.map((leg, index) => (
                <LegInputRow
                  key={index}
                  leg={leg}
                  index={index}
                  onUpdate={updateNewLeg}
                  onRemove={removeNewLeg}
                  canRemove={newLegs.length > 1}
                  label="Leg"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calculate Button */}
      <Card className="glass-effect border-0 shadow-lg">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Brain className="h-5 w-5 text-blue-600" />
              <ModelSelector />
            </div>
            
            <Button
              onClick={calculateRoll}
              disabled={loading}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 px-8"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculate Roll Outcome
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-6 fade-in">
          {/* Summary Card */}
          <Card className="glass-effect border-0 shadow-xl overflow-hidden">
            <div className={`h-2 ${result.roll_recommendation === 'RECOMMENDED' ? 'bg-green-500' : result.roll_recommendation === 'NOT_RECOMMENDED' ? 'bg-red-500' : 'bg-yellow-500'}`} />
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center text-xl">
                  <ArrowLeftRight className="h-6 w-6 mr-2 text-blue-600" />
                  Roll Analysis: {result.symbol}
                </CardTitle>
                <div className="flex items-center space-x-3">
                  <Badge className="text-lg px-4 py-1" variant="outline">
                    ${result.current_price}
                  </Badge>
                  <Badge 
                    className={`text-lg px-4 py-1 ${
                      result.roll_recommendation === 'RECOMMENDED' ? 'bg-green-500 text-white' :
                      result.roll_recommendation === 'NOT_RECOMMENDED' ? 'bg-red-500 text-white' :
                      'bg-yellow-500 text-white'
                    }`}
                  >
                    {result.roll_recommendation?.replace('_', ' ')}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Net Roll Credit/Debit */}
              <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-center mb-4">Net Roll Transaction</h3>
                <div className="flex items-center justify-center space-x-6">
                  <div className="text-center">
                    <p className="text-sm text-red-600">Close Current</p>
                    <p className="text-2xl font-bold text-red-600">
                      {result.current_position?.close_cost >= 0 ? '-' : '+'}${Math.abs(result.current_position?.close_cost || 0).toFixed(2)}
                    </p>
                  </div>
                  <ArrowRight className="h-8 w-8 text-gray-400" />
                  <div className="text-center">
                    <p className="text-sm text-green-600">Open New</p>
                    <p className="text-2xl font-bold text-green-600">
                      {result.new_position?.open_cost >= 0 ? '+' : '-'}${Math.abs(result.new_position?.open_cost || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="text-4xl font-bold text-gray-400">=</div>
                  <div className={`text-center p-4 rounded-lg ${result.net_roll_credit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                    <p className="text-sm text-gray-600">Net {result.net_roll_credit >= 0 ? 'Credit' : 'Debit'}</p>
                    <p className={`text-3xl font-bold ${result.net_roll_credit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${Math.abs(result.net_roll_credit || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Comparison Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Current Position Summary */}
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-700 mb-3 flex items-center">
                    <XCircle className="h-4 w-4 mr-2" />
                    Current Position
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Expiration:</span>
                      <span className="font-medium">{result.current_position?.expiration} ({result.current_position?.dte} DTE)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Entry Cost/Credit:</span>
                      <span className="font-medium">${result.current_position?.entry_value?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Current Value:</span>
                      <span className="font-medium">${result.current_position?.current_value?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-gray-600">Unrealized P/L:</span>
                      <span className={`font-bold ${result.current_position?.unrealized_pl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {result.current_position?.unrealized_pl >= 0 ? '+' : ''}${result.current_position?.unrealized_pl?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Profit:</span>
                      <span className="font-medium text-green-600">${result.current_position?.max_profit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Loss:</span>
                      <span className="font-medium text-red-600">${result.current_position?.max_loss}</span>
                    </div>
                  </div>
                </div>

                {/* New Position Summary */}
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-700 mb-3 flex items-center">
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    New Position
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Expiration:</span>
                      <span className="font-medium">{result.new_position?.expiration} ({result.new_position?.dte} DTE)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Open Cost/Credit:</span>
                      <span className="font-medium">${result.new_position?.open_cost?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Adjusted Cost Basis:</span>
                      <span className="font-medium">${result.new_position?.adjusted_cost_basis?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-gray-600">Potential P/L:</span>
                      <span className={`font-bold ${result.new_position?.potential_pl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {result.new_position?.potential_pl >= 0 ? '+' : ''}${result.new_position?.potential_pl?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Profit:</span>
                      <span className="font-medium text-green-600">${result.new_position?.max_profit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Loss:</span>
                      <span className="font-medium text-red-600">${result.new_position?.max_loss}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Legs Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">CURRENT LEGS (to close):</p>
                  {result.current_position?.legs?.map((leg, i) => (
                    <div key={i} className="flex items-center justify-between bg-red-50 rounded px-3 py-1 mb-1 text-sm">
                      <span className={leg.position_type === 'long' ? 'text-green-600' : 'text-red-600'}>
                        {leg.position_type === 'long' ? 'SELL' : 'BUY'} to close
                      </span>
                      <span>{leg.quantity}x ${leg.strike} {leg.option_type}</span>
                      <span className="text-gray-600">@ ${leg.current_premium?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">NEW LEGS (to open):</p>
                  {result.new_position?.legs?.map((leg, i) => (
                    <div key={i} className="flex items-center justify-between bg-green-50 rounded px-3 py-1 mb-1 text-sm">
                      <span className={leg.position_type === 'long' ? 'text-green-600' : 'text-red-600'}>
                        {leg.position_type === 'long' ? 'BUY' : 'SELL'} to open
                      </span>
                      <span>{leg.quantity}x ${leg.strike} {leg.option_type}</span>
                      <span className="text-gray-600">@ ${leg.premium?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AI Analysis */}
          <Card className="glass-effect border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Brain className="h-5 w-5 mr-2 text-blue-600" />
                AI Roll Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              {result.ai_analysis?.summary && (
                <div className={`rounded-lg p-4 mb-4 ${
                  result.roll_recommendation === 'RECOMMENDED' ? 'bg-green-50 border border-green-200' :
                  result.roll_recommendation === 'NOT_RECOMMENDED' ? 'bg-red-50 border border-red-200' :
                  'bg-yellow-50 border border-yellow-200'
                }`}>
                  <p className="font-medium">{result.ai_analysis.summary}</p>
                </div>
              )}

              {result.ai_analysis?.pros && result.ai_analysis.pros.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-semibold text-green-700 mb-2">✅ Pros of This Roll:</p>
                  <ul className="space-y-1">
                    {result.ai_analysis.pros.map((pro, i) => (
                      <li key={i} className="text-sm text-gray-700 flex items-start">
                        <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        {pro}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.ai_analysis?.cons && result.ai_analysis.cons.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-semibold text-red-700 mb-2">❌ Cons of This Roll:</p>
                  <ul className="space-y-1">
                    {result.ai_analysis.cons.map((con, i) => (
                      <li key={i} className="text-sm text-gray-700 flex items-start">
                        <XCircle className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        {con}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.ai_analysis?.alternative && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-sm font-semibold text-blue-700 mb-1">💡 Alternative Suggestion:</p>
                  <p className="text-sm text-gray-700">{result.ai_analysis.alternative}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comparison Table */}
          <Card className="glass-effect border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Target className="h-5 w-5 mr-2 text-purple-600" />
                Side-by-Side Comparison
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Metric</th>
                      <th className="text-center py-2 px-3 bg-red-50">Current</th>
                      <th className="text-center py-2 px-3 bg-green-50">New (After Roll)</th>
                      <th className="text-center py-2 px-3">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 px-3">Days to Expiration</td>
                      <td className="text-center py-2 px-3 bg-red-50">{result.current_position?.dte}</td>
                      <td className="text-center py-2 px-3 bg-green-50">{result.new_position?.dte}</td>
                      <td className="text-center py-2 px-3 font-medium">
                        +{(result.new_position?.dte || 0) - (result.current_position?.dte || 0)} days
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3">Max Profit</td>
                      <td className="text-center py-2 px-3 bg-red-50">${result.current_position?.max_profit}</td>
                      <td className="text-center py-2 px-3 bg-green-50">${result.new_position?.max_profit}</td>
                      <td className="text-center py-2 px-3 font-medium">
                        {result.comparison?.max_profit_change}
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3">Max Loss</td>
                      <td className="text-center py-2 px-3 bg-red-50">${result.current_position?.max_loss}</td>
                      <td className="text-center py-2 px-3 bg-green-50">${result.new_position?.max_loss}</td>
                      <td className="text-center py-2 px-3 font-medium">
                        {result.comparison?.max_loss_change}
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3">Breakeven</td>
                      <td className="text-center py-2 px-3 bg-red-50">{result.current_position?.breakeven}</td>
                      <td className="text-center py-2 px-3 bg-green-50">{result.new_position?.breakeven}</td>
                      <td className="text-center py-2 px-3 font-medium">
                        {result.comparison?.breakeven_change}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-gray-500">
            Analysis generated on {new Date(result.timestamp).toLocaleString()} using {result.ai_model === 'gemini' ? 'Gemini 2.5 Pro' : 'Claude Sonnet'}
          </p>
        </div>
      )}

      {/* Trading Chart Modal */}
      <TradingChartModal
        isOpen={isChartOpen}
        onClose={() => setIsChartOpen(false)}
        symbol={symbol}
      />
    </div>
  );
};

export default RollCalculator;
